import { Info } from "lucide-react";
import { format } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BundleUsage } from "@/pages/users/types/user";

interface PaymentsSummaryProps {
  paymentSummary: Record<string, number>;
  bundlesGroupedByName: Record<string, BundleUsage[]>;
}

export function PaymentsSummary({
  paymentSummary,
  bundlesGroupedByName,
}: PaymentsSummaryProps) {
  return (
    <div className="pt-2 border-t border-white/20">
      <div className="flex justify-between items-center gap-1.5 mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-white/90">Payments</span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-white/60 hover:text-white/90 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <div className="space-y-3">
                  <p className="font-semibold text-xs">Payments by Bundle:</p>
                  {Object.entries(bundlesGroupedByName)
                    .filter(([_, bundles]) => bundles.some(b => b.payment))
                    .map(([bundleName, bundles]) => (
                      <div key={bundleName} className="space-y-1.5">
                        <div className="font-medium text-xs">{bundleName}</div>
                        <div className="text-xs font-medium text-muted-foreground">Txn history:</div>
                        <div className="pl-2 space-y-1">
                          {bundles
                            .filter(bundle => bundle.payment)
                            .map((bundle) => (
                              <div key={bundle.id} className="text-xs text-muted-foreground">
                                {new Intl.NumberFormat('en-IN', {
                                  style: 'currency',
                                  currency: bundle.payment!.currency,
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(bundle.payment!.amount / 100)}
                                {' --- '}
                                {format(new Date(bundle.start_date), "MMM dd, yyyy HH:mm")}
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  {Object.values(bundlesGroupedByName).flat().filter(b => b.payment).length === 0 && (
                    <p className="text-xs text-muted-foreground">No payments found</p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {Object.keys(paymentSummary).length > 0 &&
          Object.entries(paymentSummary).map(([currency, amount]) => (
            <div key={currency} className="flex items-center justify-end">
              <span className="text-sm font-semibold text-white/90">
                {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(amount)}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

